// "Get Featured" launcher: pick one of your last 12 completed try-ons OR
// upload a brand-new custom photo (costs 5 credits or a streak pass).
// Used from Style AND Aura tabs.
import { useEffect, useState } from "react";
import { Crown, Loader2, Sparkles, Camera, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserPerks } from "@/hooks/useUserPerks";
import { useSignedImage } from "@/hooks/useSignedImage";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { SubmitFeaturedSheet } from "@/components/history/SubmitFeaturedSheet";
import { SubmitCustomFeaturedSheet } from "@/components/featured/SubmitCustomFeaturedSheet";
import { isNativeApp } from "@/lib/platform";
import { GetTheAppDialog } from "@/components/GetTheAppDialog";

interface Row {
  id: string;
  generated_image_url: string | null;
  product_style: string | null;
  created_at: string;
}

const Tile = ({
  row,
  onPick,
  selected,
}: {
  row: Row;
  onPick: () => void;
  selected: boolean;
}) => {
  const url = useSignedImage("tryon-results", row.generated_image_url);
  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(
        "group relative aspect-[3/4] overflow-hidden rounded-2xl ring-1 ring-white/10 transition-all active:scale-95",
        selected && "ring-2 ring-primary",
      )}
    >
      {url ? (
        <img src={url} alt={row.product_style ?? "Try-on"} className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <div className="h-full w-full animate-pulse bg-muted" />
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5 text-left">
        <p className="line-clamp-1 text-[11px] font-semibold text-white">
          {row.product_style ?? "Try-on"}
        </p>
      </div>
    </button>
  );
};

export const SubmitFeaturedLauncher = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) => {
  const { user } = useAuth();
  const { wallet } = useUserPerks();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [customOpen, setCustomOpen] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setRows(null);
    setPickedId(null);
    setCustomOpen(false);
    supabase
      .from("tryons")
      .select("id,generated_image_url,product_style,created_at,status,kind")
      .eq("user_id", user.id)
      .eq("status", "complete")
      .not("generated_image_url", "is", null)
      .order("created_at", { ascending: false })
      .limit(12)
      .then(({ data }) => setRows((data ?? []) as Row[]));
  }, [open, user]);

  const hasCustomPass = wallet.customFeaturedPasses > 0;

  // Web (desktop + mobile web) — Get Featured is Android-app-only.
  if (!isNativeApp()) {
    return (
      <GetTheAppDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Get Featured in the app"
        description="Submitting featured looks is available in the FitAuraAI Android app."
      />
    );
  }

  return (
    <>
      <Sheet open={open && !pickedId && !customOpen} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="rounded-t-3xl border-t border-white/10 max-h-[88vh] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-primary" /> Get Featured
            </SheetTitle>
            <SheetDescription>
              Submit one of your try-ons, or upload a custom photo. Approved looks reach everyone on the Featured reels — brands may DM you on Instagram.
            </SheetDescription>
          </SheetHeader>

          {/* Custom upload CTA */}
          <button
            type="button"
            onClick={() => setCustomOpen(true)}
            className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/10 to-accent/10 p-3 text-left transition hover:from-primary/20"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/20 text-primary">
              <Camera className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold leading-tight">Upload a custom photo</p>
              <p className="text-[11px] text-muted-foreground">
                {hasCustomPass
                  ? `Free with your streak pass (${wallet.customFeaturedPasses} left)`
                  : "5 credits · or earn a free pass with a 14-day streak"}
              </p>
            </div>
            <Upload className="h-4 w-4 text-muted-foreground" />
          </button>

          <div className="mt-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Or pick a recent try-on · 3 credits (free with 7-day streak pass)
            </p>
            {!rows ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading your looks…
              </div>
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-white/10 py-10 text-center text-sm text-muted-foreground">
                <Sparkles className="h-6 w-6 text-primary" />
                <p>You don't have any completed try-ons yet.</p>
                <p className="text-xs">Generate a look from the Style tab, or upload a custom photo above.</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {rows.map((r) => (
                  <Tile key={r.id} row={r} selected={false} onPick={() => setPickedId(r.id)} />
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <SubmitFeaturedSheet
        tryonId={pickedId}
        onClose={() => {
          setPickedId(null);
          onOpenChange(false);
        }}
        onSubmitted={() => setPickedId(null)}
      />

      <SubmitCustomFeaturedSheet
        open={customOpen}
        onClose={() => {
          setCustomOpen(false);
          onOpenChange(false);
        }}
      />
    </>
  );
};
